// §6 — the skin contract, plus the window-geometry types `WindowManagerPolicy` is written in
// terms of. Those live here rather than in the kernel because `shared/**` may import nothing
// (§6.5) and both transports need them (D-050).
import { z } from 'zod';

export const SKIN_IDS = ['house', 'windows11', 'macos'] as const;
export type SkinId = (typeof SKIN_IDS)[number];

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';

// ── §5.1 window geometry ───────────────────────────────────────────────────────────────────

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type WinState = 'normal' | 'minimized' | 'maximized' | 'snapped' | 'fullscreenSpace';

export type Edge = 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export interface SnapZone {
  id: string;
  /** Fractions of the display rect, 0..1. */
  rect: Rect;
}

export interface SnapLayout {
  id: string;
  label: string;
  zones: SnapZone[];
}

/** §5.1 — the window record. */
export interface Win {
  id: string;
  appId: string;
  /** Stable across desktop moves and minimize; keys the `.win-host` element (§5.7). */
  instanceKey: string;
  title: string;
  iconSvg: string;
  dirty: boolean;
  busy: boolean;
  rect: Rect;
  restoreRect: Rect | null;
  state: WinState;
  snap: { zoneId: string; groupId: string } | null;
  desktopId: string;
  z: number;
  minW: number;
  minH: number;
  resizable: boolean;
  createdAt: number;
  focusedAt: number;
}

export interface Desktop {
  id: string;
  name: string;
  order: number;
}

// ── §5.4 window semantics ──────────────────────────────────────────────────────────────────

export interface WindowManagerPolicy {
  onMaximizeButton(w: Win): 'maximize' | 'enterFullscreenSpace';
  onEdgeDrag(edge: Edge, w: Win, display: Rect): SnapZone | null;
  snapZones(display: Rect): SnapLayout[];
  switcherModel(): 'windows' | 'apps' | 'spaces';
  minimizeTarget(w: Win): 'barSlot';
  chromeSide(): 'left' | 'right';
  snapAssist(): boolean;
}

// ── §6.4 keymap ────────────────────────────────────────────────────────────────────────────

export interface Binding {
  id: string;
  /** Canonical chord form, e.g. `Meta+Shift+V` (§6.4). */
  chord: string;
  when?: string;
  /** Claimed by the shell; forwarded by external apps (§9.7). */
  reserved?: boolean;
}

export interface Keymap {
  id: SkinId;
  bindings: Binding[];
}

// ── §15 sound ──────────────────────────────────────────────────────────────────────────────

export const SOUND_IDS = [
  'notify',
  'error',
  'snap',
  'trash',
  'screenshot',
  'deviceConnect',
  'startup',
] as const;
export type SoundId = (typeof SOUND_IDS)[number];

/** §15 / D-043 — a sound is a WebAudio recipe, never an audio file. */
export interface SoundTone {
  wave: 'sine' | 'square' | 'sawtooth' | 'triangle';
  /** Hz at the start of the tone. */
  freq: number;
  /** Hz at the end of the tone; omit for a constant pitch. */
  endFreq?: number;
  /** Seconds after the start of the recipe. */
  at: number;
  /** Seconds. */
  duration: number;
  /** Peak gain, 0..1. */
  gain: number;
  /** Attack ramp in seconds, inside `duration`. */
  attack?: number;
  /** Release ramp in seconds, inside `duration`. */
  release?: number;
}

export interface SoundRecipe {
  tones: SoundTone[];
}

// ── §6.2 token set — complete and closed ───────────────────────────────────────────────────
//
// Any value not in this list is a spec bug. Adding one means adding it to all three skins,
// which `tests/arch/tokens.test.ts` enforces.

export const TOKEN_NAMES = [
  // Surfaces
  '--surface-desktop',
  '--surface-bar',
  '--surface-window',
  '--surface-window-inactive',
  '--surface-panel',
  '--surface-overlay',
  '--surface-input',
  '--surface-hover',
  '--surface-active',
  // Text
  '--text-primary',
  '--text-secondary',
  '--text-disabled',
  '--text-on-accent',
  // Accent
  '--accent',
  '--accent-hover',
  '--accent-active',
  '--accent-subtle',
  // Border
  '--border-subtle',
  '--border-strong',
  '--border-focus',
  // Status
  '--status-danger',
  '--status-warning',
  '--status-success',
  '--status-info',
  // Radius
  '--radius-window',
  '--radius-panel',
  '--radius-control',
  '--radius-sm',
  // Shadow
  '--shadow-window',
  '--shadow-window-focused',
  '--shadow-overlay',
  '--shadow-menu',
  // Space
  '--space-1',
  '--space-2',
  '--space-3',
  '--space-4',
  '--space-5',
  '--space-6',
  // Type
  '--font-ui',
  '--font-mono',
  '--text-xs',
  '--text-sm',
  '--text-base',
  '--text-lg',
  '--text-xl',
  '--weight-normal',
  '--weight-medium',
  '--weight-semibold',
  // Motion
  '--motion-fast',
  '--motion-base',
  '--motion-slow',
  '--ease-standard',
  '--ease-decelerate',
  '--ease-accelerate',
  // Metrics
  '--bar-height',
  '--titlebar-height',
  '--resize-handle',
  '--icon-sm',
  '--icon-md',
  '--icon-lg',
  // Material
  '--material-bar',
  '--material-overlay',
] as const;

export type TokenName = (typeof TOKEN_NAMES)[number];
export type TokenSet = Record<TokenName, string>;

// ── §6.1 the skin contract ─────────────────────────────────────────────────────────────────

/**
 * A skin component. Typed structurally rather than as a React `FC` so that `shared/**` keeps
 * its zero-dependency rule (§6.5, §2) — a React function component is assignable to this.
 */
export type SkinComponent<P = Record<string, never>> = (props: P) => unknown;

/** §19.4 — a shell-drawn dialog. Apps cannot draw here and cannot forge one. */
export interface DialogRequest {
  id: string;
  kind: 'alert' | 'confirm' | 'prompt';
  message: string;
  /** `prompt` only. */
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface DialogProps {
  request: DialogRequest;
  /**
   * The app that asked, or null for a dialog the shell raised itself. §6.3 places a dialog
   * differently per skin — house centres it, Windows puts it *in the window*, macOS slides it out
   * of the title bar — and none of the last two are expressible without knowing whose window it
   * is (D-109).
   */
  appId: string | null;
  /** `value` is supplied for `prompt`. */
  onAccept: (value?: string) => void;
  onCancel: () => void;
}

export interface SkinComponents {
  DesktopSurface: SkinComponent;
  Bar: SkinComponent;
  Launcher: SkinComponent;
  WindowFrame: SkinComponent<{ win: Win }>;
  Dialog: SkinComponent<DialogProps>;
  NotificationHost: SkinComponent;
  Switcher: SkinComponent;
  TrayFlyout: SkinComponent;
  LoginScreen: SkinComponent;
  LockScreen: SkinComponent;
}

export const SKIN_COMPONENT_NAMES = [
  'DesktopSurface',
  'Bar',
  'Launcher',
  'WindowFrame',
  'Dialog',
  'NotificationHost',
  'Switcher',
  'TrayFlyout',
  'LoginScreen',
  'LockScreen',
] as const satisfies readonly (keyof SkinComponents)[];

export const POLICY_METHOD_NAMES = [
  'onMaximizeButton',
  'onEdgeDrag',
  'snapZones',
  'switcherModel',
  'minimizeTarget',
  'chromeSide',
  'snapAssist',
] as const satisfies readonly (keyof WindowManagerPolicy)[];

export interface Skin {
  id: SkinId;
  label: string;
  /** The skin's `tokens.css` text — every §6.2 custom property on `:root[data-skin=<id>]`. */
  tokens: string;
  keymap: Keymap;
  policy: WindowManagerPolicy;
  components: SkinComponents;
  /** D-043: generated WebAudio recipes, never files. */
  sounds: Record<SoundId, SoundRecipe>;
}

// ── §5.6 session restore ───────────────────────────────────────────────────────────────────
//
// The persisted shape, and the schema that parses it back. Here rather than in the kernel
// because §22 requires boundary input to be parsed by a zod schema in `shared/`, and because
// §5.6 persists the *same* shape two ways: `session_state` on the server for a signed-in user,
// and `/System/.session.json` in OPFS for a guest. One schema, both readers.

export const SESSION_VERSION = 1 as const;

const rectSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  w: z.number().finite(),
  h: z.number().finite(),
});

const winStateSchema = z.enum(['normal', 'minimized', 'maximized', 'snapped', 'fullscreenSpace']);

export const sessionWindowSchema = z.object({
  id: z.string().min(1),
  appId: z.string().min(1),
  instanceKey: z.string().min(1),
  rect: rectSchema,
  state: winStateSchema,
  snap: z.object({ zoneId: z.string(), groupId: z.string() }).nullable(),
  /**
   * Not in §5.6's field list, and needed anyway: §5.4 requires unsnapping to return a window to
   * the rect it had *before* any of it, and that rect exists nowhere else (D-092).
   */
  restoreRect: rectSchema.nullable(),
  desktopId: z.string().min(1),
  z: z.number().int(),
});

export const sessionStateSchema = z.object({
  v: z.literal(SESSION_VERSION),
  desktops: z
    .array(z.object({ id: z.string().min(1), name: z.string(), order: z.number().int() }))
    .min(1),
  activeDesktop: z.string().min(1),
  windows: z.array(sessionWindowSchema),
  /**
   * Opaque per-instance blobs from `HangarAPI.session.save`. `unknown` on purpose: the shell
   * must never look inside one, and a blob it cannot understand must not fail the whole parse —
   * §5.6's "restore never blocks the desktop" starts here.
   */
  appState: z.record(z.string(), z.unknown()),
  /**
   * §9.5 picker grants, per window instance. Also not in §5.6's field list, and also needed:
   * without them a restored window cannot read the document it was restored holding, which
   * makes `appState` useless to every app that works on user files (D-092).
   */
  grants: z.record(z.string(), z.array(z.string())),
  focused: z.string().nullable(),
});

export type SessionWindow = z.infer<typeof sessionWindowSchema>;
export type SessionState = z.infer<typeof sessionStateSchema>;

/** Parse a persisted session, or `null` if it is missing, truncated or from another version. */
export function parseSessionState(text: string): SessionState | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  const parsed = sessionStateSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
